import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, QuestionMarkCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const HelpCenter = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "How do I get started with W3Skills?",
      answer: "Simply sign up for a free account, complete your profile, and start with our basic concepts. You can upgrade to Premium or Pro plans anytime for advanced features."
    },
    {
      question: "What subjects are covered in the platform?",
      answer: "We cover all three main subjects for MH-CET: Physics, Chemistry, and Mathematics. Each subject has comprehensive chapters with practice questions and explanations."
    },
    {
      question: "How accurate is the CET Score Predictor?",
      answer: "Our score predictor has 95% accuracy based on historical data and thousands of student performances. It considers your mock test scores, subject performance, and study patterns."
    },
    {
      question: "Can I access the platform on mobile devices?",
      answer: "Yes! W3Skills is fully responsive and works perfectly on smartphones, tablets, and desktop computers. You can study anywhere, anytime."
    },
    {
      question: "How often are mock tests updated?",
      answer: "We regularly update our mock test database with new questions and previous year papers. Premium and Pro users get access to the latest tests first."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit/debit cards, UPI, net banking, and digital wallets through our secure payment gateway. All transactions are encrypted and secure."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. You'll continue to have access to your plan features until the end of your current billing period."
    },
    {
      question: "How do I contact support?",
      answer: "You can reach our support team at w3skills.devstudio@gmail.com. We typically respond within 24 hours during business days."
    }
  ];

  const categories = [
    {
      title: "Getting Started",
      icon: "🚀",
      description: "Learn how to set up your account and begin your CET preparation journey."
    },
    {
      title: "Account & Billing",
      icon: "💳",
      description: "Manage your subscription, payment methods, and account settings."
    },
    {
      title: "Study Features",
      icon: "📚",
      description: "Understand how to use our learning tools, mock tests, and analytics."
    },
    {
      title: "Technical Support",
      icon: "🔧",
      description: "Get help with technical issues, app problems, and troubleshooting."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back
              </button>
            </div>
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-blue-600">W3Skills</h1>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <QuestionMarkCircleIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Help Center
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions and get the support you need to make the most of your CET preparation.
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How can we help you?
            </h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help articles..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
                🔍
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Browse by Category
            </h2>
            <p className="text-xl text-gray-600">
              Find help articles organized by topic for easier navigation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                <div className="text-4xl mb-4">{category.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.title}</h3>
                <p className="text-gray-600 text-sm">{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to the most common questions about W3Skills.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Need Help?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our support team is here to help you with any questions or issues.
          </p>
          <div className="bg-white rounded-lg p-8 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h3>
            <p className="text-gray-600 mb-4">
              Email us at:
            </p>
            <a
              href="mailto:w3skills.devstudio@gmail.com"
              className="text-blue-600 font-semibold hover:underline"
            >
              w3skills.devstudio@gmail.com
            </a>
            <p className="text-sm text-gray-500 mt-4">
              We typically respond within 24 hours during business days.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
